import { Card as AntdCard, Typography, Button } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, TrashCan } from '@carbon/icons-react';

// Styles
import './Card.module.less';

export default function Card(props) {
  const {
    noBodyPadding,
    color = undefined,
    className,
    children,
    collapsible,
    defaultOpen,
    open: controlledOpen,
    onOpenChange,
    lightInMobile,
    subtitle,
    stickyHeader,
    ...restProps
  } = props;

  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? true);
  const cardRef = useRef(null);

  // Use controlled or uncontrolled state
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleToggle = useCallback(
    (newOpen) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange],
  );

  useEffect(() => {
    const toggleCollapse = (e) => {
      // Don't toggle if clicking on the extra content area
      if (e.target.closest('.ant-card-extra')) {
        return;
      }

      if (collapsible) {
        handleToggle(!open);
      }
    };

    if (collapsible && cardRef.current) {
      const header = cardRef.current.querySelector('.ant-card-head');
      if (header) {
        header.addEventListener('click', toggleCollapse);
        return () => {
          header.removeEventListener('click', toggleCollapse);
        };
      }
    }
  }, [collapsible, open, handleToggle]);

  const renderTitle = () => {
    if (!restProps.title) return null;

    const titleContent = subtitle ? (
      <div className="py-4 min-w-0">
        <div className="font-book text-base whitespace-pre-wrap">{restProps.title}</div>
        <div className="text-sm text-gray-500 font-normal whitespace-pre-wrap">{subtitle}</div>
      </div>
    ) : (
      restProps.title
    );

    if (collapsible) {
      return (
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={20} /> : <ChevronDown size={20} className="-rotate-90" />}
          {subtitle ? titleContent : <span>{titleContent}</span>}
        </div>
      );
    }

    return titleContent;
  };

  return (
    <AntdCard
      ref={cardRef}
      className={classNames(
        {
          'no-body-padding': noBodyPadding || (collapsible && !open),
          'no-heading-border': !children || (collapsible && !open),
          'light-in-mobile': lightInMobile,
          'collapsible-card': collapsible,
          'sticky-header': stickyHeader,
          collapsed: open === false,
          ...(color === 'primary' ? { 'primary-card': true } : {}),
          ...(color === 'error' ? { 'error-card': true } : {}),
          ...(color === 'success' ? { 'success-card': true } : {}),
        },
        className,
      )}
      {...restProps}
      title={renderTitle()}
      extra={restProps.extra ? <div onClick={(e) => e.stopPropagation()}>{restProps.extra}</div> : undefined}
    >
      {children}
    </AntdCard>
  );
}

Card.propTypes = {
  noBodyPadding: PropTypes.bool,
  color: PropTypes.string,
  stickyHeader: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  variant: PropTypes.oneOf(['outlined', 'borderless']),
};

export function CardTitle({ title, description, icon, className, titleProps, descriptionProps }) {
  return (
    <div className={classNames('flex items-start gap-4 my-6', className)}>
      {icon && <div className="text-3xl leading-none">{icon}</div>}

      <div>
        <Typography.Title
          level={3}
          {...titleProps}
          className={classNames(
            '!mb-0 whitespace-pre-wrap break-words !text-xl !leading-normal font-semibold',
            titleProps?.className,
          )}
        >
          {title}
        </Typography.Title>

        {description && (
          <Typography.Text
            type="secondary"
            {...descriptionProps}
            className={classNames('block font-normal whitespace-pre-wrap break-words', descriptionProps?.className)}
          >
            {description}
          </Typography.Text>
        )}
      </div>
    </div>
  );
}

export function CardFooter({ className, children, style }) {
  return (
    <div className={classNames('bg-[#f3f3f3a6] border-solid border-t border-t-[#e7e7e7]', className)} style={style}>
      {children}
    </div>
  );
}

export { CardEdit } from './CardEdit';

export function CardDelete({ onConfirm, title = 'Delete', description = 'Are you sure?' }) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = () => {
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    onConfirm();
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  if (showConfirm) {
    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Button variant="outlined" size="small" onClick={handleConfirm} danger>
          Confirm
        </Button>

        <Button variant="outlined" size="small" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Button
      type="text"
      icon={<TrashCan size={18} />}
      className="text-gray-400 hover:text-red-600"
      onClick={handleDelete}
      danger
    />
  );
}
